import React from 'react';
import { Button } from '../../../components/ui/button';
import { Plus, Edit2, Trash2, X, Upload, Trophy, Calendar, Clock, Image } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import ImagePickerModal from './ImagePickerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get proper image URL for uploads
const getUploadImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  // NEW: Static menu uploads (persisted in git) - use as-is
  if (imageUrl.startsWith('/images/menu-uploads/')) {
    return imageUrl;
  }
  // OLD: Backend-served uploads - add /api prefix if needed
  if (imageUrl.startsWith('/images/uploads/')) {
    return `/api${imageUrl}`;
  }
  if (imageUrl.startsWith('/api/images/uploads/')) {
    return imageUrl;
  }
  return imageUrl;
};

const SpecialsAdmin = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = React.useState('limited_offer');
  const [specials, setSpecials] = React.useState([]);
  const [allMenuItems, setAllMenuItems] = React.useState([]);
  const [promotions, setPromotions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showPromoDialog, setShowPromoDialog] = React.useState(false);
  const [showAddToSpecialsDialog, setShowAddToSpecialsDialog] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [editingPromo, setEditingPromo] = React.useState(null);
  const [promoForm, setPromoForm] = React.useState({
    title: '',
    description: '',
    image_url: '',
    youtube_url: '',
    promo_type: 'announcement',
    start_date: '',
    end_date: '',
    is_active: true,
    order: 0
  });
  const [isDragging, setIsDragging] = React.useState(false);
  const [expandedTypes, setExpandedTypes] = React.useState({
    announcement: true,
    contest: true,
    limited_offer: true,
    event: true
  });
  
  // Image picker state
  const [showImagePicker, setShowImagePicker] = React.useState(false);
  const [imagePickerTarget, setImagePickerTarget] = React.useState(null); // 'offer-0', 'offer-1', 'offer-2', 'winner', 'event-0', etc.
  
  // New state for Limited Time Offer
  const [limitedOffer, setLimitedOffer] = React.useState({
    title: '',
    description: '',
    images: [],
    image_captions: [],
    caption_position: 'below',
    is_active: true
  });
  
  // New state for Winners
  const [winners, setWinners] = React.useState([]);
  const [showWinnerDialog, setShowWinnerDialog] = React.useState(false);
  const [editingWinner, setEditingWinner] = React.useState(null);
  const [winnerForm, setWinnerForm] = React.useState({
    name: '',
    image_url: '',
    prize: '',
    is_current: false
  });
  
  // New state for Events
  const [events, setEvents] = React.useState([]);
  const [showEventDialog, setShowEventDialog] = React.useState(false);
  const [editingEvent, setEditingEvent] = React.useState(null);
  const [eventForm, setEventForm] = React.useState({
    title: '',
    description: '',
    images: [],
    is_active: true
  });
  
  // New state for Featured Video
  const [featuredVideo, setFeaturedVideo] = React.useState({
    title: '',
    subtitle: '',
    video_url: '',
    poster_url: '',
    is_active: true
  });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch ALL menu items
      const menuRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (menuRes.ok) {
        const data = await menuRes.json();
        setAllMenuItems(data);
        setSpecials(data.filter(item => item.famous || item.featured));
      }

      // Fetch promotions
      const promoRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promotions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (promoRes.ok) {
        setPromotions(await promoRes.json());
      }
      
      // Fetch Limited Time Offer
      const offerRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/limited-offer`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (offerRes.ok) {
        const offerData = await offerRes.json();
        if (offerData) {
          setLimitedOffer(offerData);
        }
      }
      
      // Fetch Winners
      const winnersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/winners/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (winnersRes.ok) {
        setWinners(await winnersRes.json());
      }
      
      // Fetch Events
      const eventsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/events/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (eventsRes.ok) {
        setEvents(await eventsRes.json());
      }
      
      // Fetch Featured Video
      const videoRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/featured-video`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (videoRes.ok) {
        const videoData = await videoRes.json();
        if (videoData) {
          setFeaturedVideo(videoData);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  // Helper function to upload image to server
  const uploadImageToServer = async (file) => {
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 5MB', variant: 'destructive' });
      return null;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (response.ok) {
        const result = await response.json();
        toast({ title: 'Image uploaded' });
        return result.url;
      } else {
        let errorMessage = 'Failed to upload';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          // Response body might already be consumed or not JSON
        }
        toast({ title: 'Upload failed', description: errorMessage, variant: 'destructive' });
        return null;
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Upload failed', description: 'Network error', variant: 'destructive' });
      return null;
    }
  };

  const toggleFamous = async (itemId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/${itemId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ famous: !currentStatus })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const toggleFeatured = async (itemId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/${itemId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ featured: !currentStatus })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  // State for editing menu item specials
  const [editingMenuSpecial, setEditingMenuSpecial] = React.useState(null);
  const [menuSpecialForm, setMenuSpecialForm] = React.useState({
    special_description: '',
    special_price: '',
    special_start_date: '',
    special_end_date: ''
  });
  const [showMenuSpecialDialog, setShowMenuSpecialDialog] = React.useState(false);

  const handleEditMenuSpecial = (item) => {
    setEditingMenuSpecial(item);
    setMenuSpecialForm({
      special_description: item.special_description || '',
      special_price: item.special_price || '',
      special_start_date: item.special_start_date ? item.special_start_date.split('T')[0] : '',
      special_end_date: item.special_end_date ? item.special_end_date.split('T')[0] : ''
    });
    setShowMenuSpecialDialog(true);
  };

  const handleSaveMenuSpecial = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/${editingMenuSpecial.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          special_description: menuSpecialForm.special_description || null,
          special_price: menuSpecialForm.special_price ? parseFloat(menuSpecialForm.special_price) : null,
          special_start_date: menuSpecialForm.special_start_date || null,
          special_end_date: menuSpecialForm.special_end_date || null
        })
      });
      setShowMenuSpecialDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving menu special:', error);
    }
  };

  const removeFromSpecials = async (itemId) => {
    if (!window.confirm('Remove this item from Specials?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/${itemId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          famous: false,
          featured: false,
          on_specials: false,
          special_description: null,
          special_price: null,
          special_start_date: null,
          special_end_date: null
        })
      });
      fetchData();
    } catch (error) {
      console.error('Error removing from specials:', error);
    }
  };

  const handleCreatePromo = (promoType = 'announcement') => {
    setEditingPromo(null);
    setPromoForm({
      title: '',
      description: '',
      image_url: '',
      youtube_url: '',
      promo_type: promoType,
      start_date: '',
      end_date: '',
      is_active: true,
      order: 0
    });
    setShowPromoDialog(true);
  };

  const handleEditPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({
      title: promo.title,
      description: promo.description,
      image_url: promo.image_url || '',
      youtube_url: promo.youtube_url || '',
      promo_type: promo.promo_type,
      start_date: promo.start_date ? promo.start_date.split('T')[0] : '',
      end_date: promo.end_date ? promo.end_date.split('T')[0] : '',
      is_active: promo.is_active,
      order: promo.order
    });
    setShowPromoDialog(true);
  };

  const handleSavePromo = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingPromo 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/promotions/${editingPromo.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/promotions`;
      
      const body = {
        ...promoForm,
        start_date: promoForm.start_date || null,
        end_date: promoForm.end_date || null
      };

      await fetch(url, {
        method: editingPromo ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      setShowPromoDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving promotion:', error);
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promotions/${promoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting promotion:', error);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const url = await uploadImageToServer(file);
      if (url) {
        setPromoForm({ ...promoForm, image_url: url });
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const url = await uploadImageToServer(file);
      if (url) {
        setPromoForm({ ...promoForm, image_url: url });
      }
    }
  };

  // ==========================================
  // Limited Time Offer handlers
  // ==========================================
  const handleSaveLimitedOffer = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/limited-offer`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(limitedOffer)
      });
      toast({ title: 'Limited Time Offer saved successfully!' });
      fetchData();
    } catch (error) {
      console.error('Error saving limited offer:', error);
      toast({ title: 'Error saving offer', variant: 'destructive' });
    }
  };

  const handleOfferImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const url = await uploadImageToServer(file);
      if (url) {
        const newImages = [...(limitedOffer.images || [])];
        if (index !== undefined) {
          newImages[index] = url;
        } else {
          if (newImages.length < 3) {
            newImages.push(url);
          }
        }
        setLimitedOffer({ ...limitedOffer, images: newImages });
      }
    }
  };

  const removeOfferImage = (index) => {
    const newImages = limitedOffer.images.filter((_, i) => i !== index);
    setLimitedOffer({ ...limitedOffer, images: newImages });
  };

  // ==========================================
  // Winners handlers
  // ==========================================
  const handleSaveWinner = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingWinner 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/specials-content/winners/${editingWinner.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/specials-content/winners`;
      
      await fetch(url, {
        method: editingWinner ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(winnerForm)
      });
      
      setShowWinnerDialog(false);
      setEditingWinner(null);
      setWinnerForm({ name: '', image_url: '', prize: '', is_current: false });
      fetchData();
    } catch (error) {
      console.error('Error saving winner:', error);
      alert('Error saving winner');
    }
  };

  const handleDeleteWinner = async (winnerId) => {
    if (!window.confirm('Are you sure you want to delete this winner?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/winners/${winnerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting winner:', error);
    }
  };

  const handleWinnerImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const url = await uploadImageToServer(file);
      if (url) {
        setWinnerForm({ ...winnerForm, image_url: url });
      }
    }
  };

  // ==========================================
  // Events handlers
  // ==========================================
  const handleSaveEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingEvent 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/specials-content/events/${editingEvent.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/specials-content/events`;
      
      await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventForm)
      });
      
      setShowEventDialog(false);
      setEditingEvent(null);
      setEventForm({ title: '', description: '', images: [], is_active: true });
      fetchData();
    } catch (error) {
      console.error('Error saving event:', error);
      toast({ title: 'Error saving event', variant: 'destructive' });
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  // Featured Video save handler
  const handleSaveFeaturedVideo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/featured-video/update`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(featuredVideo)
      });
      
      if (response.ok) {
        toast({ title: 'Featured video saved!' });
        fetchData();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving featured video:', error);
      toast({ title: 'Error saving video', variant: 'destructive' });
    }
  };

  // Video upload handler
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('video/')) {
      toast({ title: 'Invalid file', description: 'Please upload a video file', variant: 'destructive' });
      return;
    }
    
    // Check file size (50MB max for videos)
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum video size is 50MB', variant: 'destructive' });
      return;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      toast({ title: 'Uploading video...', description: 'This may take a moment' });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (response.ok) {
        const result = await response.json();
        setFeaturedVideo({ ...featuredVideo, video_url: result.url });
        toast({ title: 'Video uploaded successfully!' });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
  };

  // Poster image upload handler
  const handlePosterUpload = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const url = await uploadImageToServer(file);
      if (url) {
        setFeaturedVideo({ ...featuredVideo, poster_url: url });
      }
    }
  };

  const handleEventImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const url = await uploadImageToServer(file);
      if (url) {
        const newImages = [...(eventForm.images || [])];
        if (index !== undefined) {
          newImages[index] = url;
        } else {
          if (newImages.length < 6) {
            newImages.push(url);
          }
        }
        setEventForm({ ...eventForm, images: newImages });
      }
    }
  };

  const removeEventImage = (index) => {
    const newImages = eventForm.images.filter((_, i) => i !== index);
    setEventForm({ ...eventForm, images: newImages });
  };

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
    return match ? match[1] : null;
  };

  const promoTypes = [
    { value: 'menu_special', label: 'Menu Specials', icon: '⭐', color: 'bg-yellow-100 border-yellow-300', isMenuItems: true },
    { value: 'announcement', label: 'Announcements', icon: '📢', color: 'bg-blue-100 border-blue-300' },
    { value: 'contest', label: 'Contests', icon: '🏆', color: 'bg-orange-100 border-orange-300' },
    { value: 'limited_offer', label: 'Limited Time Offers', icon: '⏰', color: 'bg-red-100 border-red-300' },
    { value: 'event', label: 'Events', icon: '🎉', color: 'bg-purple-100 border-purple-300' }
  ];

  const getPromosByType = (type) => {
    return promotions.filter(p => p.promo_type === type).sort((a, b) => a.order - b.order);
  };
  
  // Get menu items that are on specials
  const getMenuSpecials = () => {
    return allMenuItems.filter(item => item.on_specials);
  };

  const toggleTypeSection = (type) => {
    setExpandedTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  // Add item to specials (famous or featured)
  const addToSpecials = async (itemId, type) => {
    try {
      const token = localStorage.getItem('token');
      const updateData = type === 'famous' ? { famous: true } : { featured: true };
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/${itemId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      fetchData();
      setShowAddToSpecialsDialog(false);
    } catch (error) {
      console.error('Error adding to specials:', error);
    }
  };

  // Handle opening winner dialog for editing
  const handleEditWinner = (winner) => {
    setEditingWinner(winner);
    setWinnerForm({
      name: winner.name || '',
      image_url: winner.image_url || '',
      prize: winner.prize || '',
      is_current: winner.is_current || false
    });
    setShowWinnerDialog(true);
  };

  // Handle opening event dialog for editing
  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      images: event.images || [],
      is_active: event.is_active !== false
    });
    setShowEventDialog(true);
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-black">Specials & Promotions</h2>
          <p className="text-gray-600 mt-1">Manage announcements, contests, offers, and featured items</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 sm:gap-2 mb-6 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('limited_offer')}
          className={`px-3 sm:px-6 py-2 sm:py-3 font-semibold transition-colors text-xs sm:text-base whitespace-nowrap ${
            activeTab === 'limited_offer' 
              ? 'border-b-2 border-red-500 text-red-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-1" />
          Limited Offer
        </button>
        <button
          onClick={() => setActiveTab('winners')}
          className={`px-3 sm:px-6 py-2 sm:py-3 font-semibold transition-colors text-xs sm:text-base whitespace-nowrap ${
            activeTab === 'winners' 
              ? 'border-b-2 border-yellow-500 text-yellow-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Trophy className="w-4 h-4 inline mr-1" />
          Winners ({winners.length})
        </button>
        <button
          onClick={() => setActiveTab('events')}
          className={`px-3 sm:px-6 py-2 sm:py-3 font-semibold transition-colors text-xs sm:text-base whitespace-nowrap ${
            activeTab === 'events' 
              ? 'border-b-2 border-purple-500 text-purple-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Calendar className="w-4 h-4 inline mr-1" />
          Events ({events.length})
        </button>
        <button
          onClick={() => setActiveTab('promotions')}
          className={`px-3 sm:px-6 py-2 sm:py-3 font-semibold transition-colors text-xs sm:text-base whitespace-nowrap ${
            activeTab === 'promotions' 
              ? 'border-b-2 border-pink-500 text-pink-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📣 Promos ({promotions.length})
        </button>
        <button
          onClick={() => setActiveTab('featured')}
          className={`px-3 sm:px-6 py-2 sm:py-3 font-semibold transition-colors text-xs sm:text-base whitespace-nowrap ${
            activeTab === 'featured' 
              ? 'border-b-2 border-pink-500 text-pink-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          ⭐ Featured ({specials.length})
        </button>
        <button
          onClick={() => setActiveTab('video')}
          className={`px-3 sm:px-6 py-2 sm:py-3 font-semibold transition-colors text-xs sm:text-base whitespace-nowrap ${
            activeTab === 'video' 
              ? 'border-b-2 border-pink-500 text-pink-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🎬 Video
        </button>
      </div>

      {/* ==========================================
          LIMITED TIME OFFER TAB
          ========================================== */}
      {activeTab === 'limited_offer' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border-2 border-red-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-8 h-8 text-red-500" />
              <div>
                <h3 className="text-xl font-bold">Limited Time Offer</h3>
                <p className="text-sm text-gray-600">Create a special offer with up to 3 images</p>
              </div>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Offer Title</label>
              <input
                type="text"
                value={limitedOffer.title || ''}
                onChange={(e) => setLimitedOffer({ ...limitedOffer, title: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-red-500 focus:outline-none"
                placeholder="e.g., Summer Special - 2 for 1 Sundaes!"
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Description</label>
              <textarea
                value={limitedOffer.description || ''}
                onChange={(e) => setLimitedOffer({ ...limitedOffer, description: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-red-500 focus:outline-none"
                rows="3"
                placeholder="Describe your limited time offer..."
              />
            </div>

            {/* Images - Up to 3 */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Images (up to 3)</label>
              
              {/* Caption Position Toggle */}
              <div className="flex items-center gap-4 mb-3 p-2 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Caption Position:</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="captionPosition"
                    value="above"
                    checked={limitedOffer.caption_position === 'above'}
                    onChange={(e) => setLimitedOffer({ ...limitedOffer, caption_position: e.target.value })}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-sm">Above Image</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="captionPosition"
                    value="below"
                    checked={limitedOffer.caption_position !== 'above'}
                    onChange={(e) => setLimitedOffer({ ...limitedOffer, caption_position: e.target.value })}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-sm">Below Image</span>
                </label>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[0, 1, 2].map((index) => (
                  <div key={index} className="relative">
                    {limitedOffer.images?.[index] ? (
                      <div className="relative rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50">
                        <img 
                          src={getUploadImageUrl(limitedOffer.images[index])} 
                          alt={limitedOffer.image_captions?.[index] || `Offer ${index + 1}`}
                          className="w-full h-auto object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                          }}
                        />
                        <div className="hidden w-full h-32 items-center justify-center bg-gray-100 text-gray-500 text-sm text-center p-2">
                          Image not found<br/><span className="text-xs">{limitedOffer.images[index]}</span>
                        </div>
                        <button
                          onClick={() => removeOfferImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 z-10"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-40 rounded-lg border-2 border-dashed border-gray-300 hover:border-red-500 transition-colors gap-2 p-2">
                        <Upload className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-500">Image {index + 1}</span>
                        <div className="flex gap-1">
                          <label className="cursor-pointer px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded">
                            Upload
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={(e) => handleOfferImageUpload(e)}
                              className="hidden"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setImagePickerTarget(`offer-${index}`);
                              setShowImagePicker(true);
                            }}
                            className="px-2 py-1 bg-purple-500 hover:bg-purple-600 text-white text-xs rounded flex items-center gap-1"
                          >
                            <Image className="w-3 h-3" />
                            Gallery
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Caption input for each image */}
                    {limitedOffer.images?.[index] && (
                      <input
                        type="text"
                        value={limitedOffer.image_captions?.[index] || ''}
                        onChange={(e) => {
                          const newCaptions = [...(limitedOffer.image_captions || ['', '', ''])];
                          newCaptions[index] = e.target.value;
                          setLimitedOffer({ ...limitedOffer, image_captions: newCaptions });
                        }}
                        className="w-full mt-2 px-3 py-2 text-sm border rounded focus:border-red-500 focus:outline-none"
                        placeholder={`Caption for image ${index + 1}...`}
                      />
                    )}
                  </div>
                ))}
              </div>
              {/* URL inputs for manual entry */}
              <div className="mt-3 space-y-2">
                <p className="text-xs text-gray-500">Or paste image URLs directly:</p>
                {[0, 1, 2].map((index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={limitedOffer.images?.[index] || ''}
                      onChange={(e) => {
                        const newImages = [...(limitedOffer.images || [])];
                        newImages[index] = e.target.value;
                        setLimitedOffer({ ...limitedOffer, images: newImages.filter(Boolean) });
                      }}
                      className="flex-1 px-3 py-2 text-sm border rounded focus:border-red-500 focus:outline-none"
                      placeholder={`Image ${index + 1} URL`}
                    />
                    <input
                      type="text"
                      value={limitedOffer.image_captions?.[index] || ''}
                      onChange={(e) => {
                        const newCaptions = [...(limitedOffer.image_captions || ['', '', ''])];
                        newCaptions[index] = e.target.value;
                        setLimitedOffer({ ...limitedOffer, image_captions: newCaptions });
                      }}
                      className="flex-1 px-3 py-2 text-sm border rounded focus:border-red-500 focus:outline-none"
                      placeholder={`Caption ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg mb-4">
              <input
                type="checkbox"
                id="offer_active"
                checked={limitedOffer.is_active !== false}
                onChange={(e) => setLimitedOffer({ ...limitedOffer, is_active: e.target.checked })}
                className="w-5 h-5 accent-red-500"
              />
              <label htmlFor="offer_active" className="font-bold cursor-pointer">
                Active (visible on Specials page)
              </label>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveLimitedOffer}
              className="w-full bg-red-500 hover:bg-red-600 text-white gap-2"
            >
              Save Limited Time Offer
            </Button>
          </div>
        </div>
      )}

      {/* ==========================================
          WINNERS TAB
          ========================================== */}
      {activeTab === 'winners' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border-2 border-yellow-200 overflow-hidden">
            <div className="p-4 bg-yellow-50 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div className="flex items-center gap-3">
                <Trophy className="w-8 h-8 text-yellow-500" />
                <div>
                  <h3 className="font-bold text-lg">Weekly Drawing Winners</h3>
                  <p className="text-sm text-gray-600">Manage current and past winners</p>
                </div>
              </div>
              <div className="flex gap-2">
                {winners.filter(w => w.is_current).length > 0 && (
                  <Button
                    onClick={async () => {
                      const currentCount = winners.filter(w => w.is_current).length;
                      if (!window.confirm(`Clear ALL ${currentCount} current winner(s)? They will be moved to past winners.`)) return;
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/winners/current/clear`, {
                          method: 'DELETE',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          const data = await response.json();
                          toast({ title: data.message });
                          fetchData();
                        }
                      } catch (error) {
                        console.error('Error clearing winners:', error);
                      }
                    }}
                    variant="outline"
                    className="border-orange-300 text-orange-600 hover:bg-orange-50 gap-2"
                  >
                    <X className="w-4 h-4" />
                    Clear Current ({winners.filter(w => w.is_current).length})
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setEditingWinner(null);
                    setWinnerForm({ name: '', image_url: '', prize: '', is_current: false });
                    setShowWinnerDialog(true);
                  }}
                  className="bg-yellow-500 hover:bg-yellow-600 gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Winner
                </Button>
              </div>
            </div>

            {winners.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No winners yet</p>
                <p className="text-sm text-gray-500 mb-4">Add your first drawing winner!</p>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {/* Current Winner */}
                {winners.filter(w => w.is_current).map((winner) => (
                  <div key={winner.id} className="p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg border-2 border-yellow-400">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="w-20 h-20 rounded-full overflow-hidden bg-yellow-200 flex-shrink-0">
                        {winner.image_url ? (
                          <img src={winner.image_url} alt={winner.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Trophy className="w-10 h-10 text-yellow-500" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="bg-yellow-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">CURRENT WINNER</span>
                        </div>
                        <h4 className="font-bold text-xl">{winner.name}</h4>
                        {winner.prize && <p className="text-gray-600">Prize: {winner.prize}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleEditWinner(winner)} variant="outline" size="sm">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          onClick={async () => {
                            if (!window.confirm(`Remove ${winner.name} from current winners? They will be moved to past winners.`)) return;
                            try {
                              const token = localStorage.getItem('token');
                              await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/winners/${winner.id}`, {
                                method: 'PUT',
                                headers: { 
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ is_current: false })
                              });
                              toast({ title: `${winner.name} moved to past winners` });
                              fetchData();
                            } catch (error) {
                              console.error('Error:', error);
                            }
                          }}
                          variant="outline" 
                          size="sm"
                          className="border-orange-300 text-orange-600 hover:bg-orange-50"
                          title="Move to past winners"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => handleDeleteWinner(winner.id)} variant="destructive" size="sm" title="Delete permanently">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Past Winners */}
                {winners.filter(w => !w.is_current).length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-bold text-gray-500 mb-3">Past Winners</h4>
                    <div className="space-y-2">
                      {winners.filter(w => !w.is_current).map((winner) => (
                        <div key={winner.id} className="p-3 bg-gray-50 rounded-lg flex flex-col sm:flex-row items-start sm:items-center gap-3">
                          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                            {winner.image_url ? (
                              <img src={winner.image_url} alt={winner.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Trophy className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold">{winner.name}</h4>
                            {winner.prize && <p className="text-sm text-gray-600">{winner.prize}</p>}
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleEditWinner(winner)} variant="outline" size="sm">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button onClick={() => handleDeleteWinner(winner.id)} variant="destructive" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==========================================
          EVENTS TAB
          ========================================== */}
      {activeTab === 'events' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border-2 border-purple-200 overflow-hidden">
            <div className="p-4 bg-purple-50 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-purple-500" />
                <div>
                  <h3 className="font-bold text-lg">Events & Galleries</h3>
                  <p className="text-sm text-gray-600">Add events with up to 6 images each</p>
                </div>
              </div>
              <Button
                onClick={() => {
                  setEditingEvent(null);
                  setEventForm({ title: '', description: '', images: [], is_active: true });
                  setShowEventDialog(true);
                }}
                className="bg-purple-500 hover:bg-purple-600 gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Event
              </Button>
            </div>

            {events.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No events yet</p>
                <p className="text-sm text-gray-500 mb-4">Add your first event with photos!</p>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {events.map((event) => (
                  <div key={event.id} className={`p-4 rounded-lg border-2 ${event.is_active ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-lg">{event.title}</h4>
                          {!event.is_active && (
                            <span className="bg-gray-300 text-gray-700 text-xs px-2 py-0.5 rounded">Inactive</span>
                          )}
                        </div>
                        {event.description && <p className="text-sm text-gray-600">{event.description}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleEditEvent(event)} variant="outline" size="sm">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => handleDeleteEvent(event.id)} variant="destructive" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Image Gallery */}
                    {event.images && event.images.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                        {event.images.map((img, idx) => (
                          <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-200">
                            <img src={img} alt={`${event.title} ${idx + 1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Promotions Tab - Organized by Type */}
      {activeTab === 'promotions' && (
        <div className="space-y-4">
          {promoTypes.map((type) => {
            const typePromos = getPromosByType(type.value);
            const isExpanded = expandedTypes[type.value];
            
            return (
              <div key={type.value} className={`border-2 rounded-xl overflow-hidden ${type.color}`}>
                {/* Section Header */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/50 transition-colors"
                  onClick={() => toggleTypeSection(type.value)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{type.icon}</span>
                    <div>
                      <h3 className="text-xl font-bold">{type.label}</h3>
                      <p className="text-sm text-gray-600">{typePromos.length} item{typePromos.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreatePromo(type.value);
                      }}
                      className="bg-pink-600 hover:bg-pink-700 gap-2"
                      size="sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add {type.label.slice(0, -1)}
                    </Button>
                    {isExpanded ? (
                      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Section Content */}
                {isExpanded && (
                  <div className="bg-white p-4 border-t">
                    {typePromos.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p className="mb-2">No {type.label.toLowerCase()} yet</p>
                        <Button
                          onClick={() => handleCreatePromo(type.value)}
                          variant="outline"
                          className="gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add First {type.label.slice(0, -1)}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {typePromos.map((promo) => (
                          <div 
                            key={promo.id} 
                            className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                              promo.is_active ? 'bg-gray-50 hover:bg-gray-100' : 'bg-gray-100 opacity-60'
                            }`}
                          >
                            {/* Image/Video Preview */}
                            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-200">
                              {promo.youtube_url ? (
                                <div className="w-full h-full flex items-center justify-center bg-red-100">
                                  <span className="text-2xl">▶️</span>
                                </div>
                              ) : promo.image_url ? (
                                <img src={promo.image_url} alt={promo.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <span className="text-2xl">{type.icon}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-bold text-lg truncate">{promo.title}</h4>
                                {!promo.is_active && (
                                  <span className="bg-gray-300 text-gray-700 text-xs px-2 py-0.5 rounded">Inactive</span>
                                )}
                                {promo.youtube_url && (
                                  <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded">📹 Video</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 truncate">{promo.description}</p>
                              {(promo.start_date || promo.end_date) && (
                                <p className="text-xs text-gray-500 mt-1">
                                  📅 {promo.start_date && new Date(promo.start_date).toLocaleDateString()}
                                  {promo.start_date && promo.end_date && ' - '}
                                  {promo.end_date && new Date(promo.end_date).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleEditPromo(promo)}
                                variant="outline"
                                size="sm"
                                className="gap-1"
                              >
                                <Edit2 className="w-4 h-4" />
                                Edit
                              </Button>
                              <Button
                                onClick={() => handleDeletePromo(promo.id)}
                                variant="destructive"
                                size="sm"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Summary */}
          <div className="mt-6 p-4 bg-gray-100 rounded-xl">
            <div className="flex justify-between items-center">
              <div>
                <span className="font-bold">Total Promotions: </span>
                <span className="text-pink-600 font-bold">{promotions.length}</span>
              </div>
              <div className="flex gap-4 text-sm text-gray-600">
                <span>Active: {promotions.filter(p => p.is_active).length}</span>
                <span>With Video: {promotions.filter(p => p.youtube_url).length}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Featured Menu Items Tab */}
      {activeTab === 'featured' && (
        <div className="space-y-4">
          {/* Currently Featured Section */}
          <div className="bg-white rounded-xl border-2 border-yellow-200 overflow-hidden">
            <div className="p-4 bg-yellow-50 border-b flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg">⭐ Currently on Specials Page</h3>
                <p className="text-sm text-gray-600">{specials.length} item{specials.length !== 1 ? 's' : ''} showing</p>
              </div>
              <Button
                onClick={() => setShowAddToSpecialsDialog(true)}
                className="bg-pink-600 hover:bg-pink-700 gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Item to Specials
              </Button>
            </div>
            
            {specials.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-2">No featured items yet</p>
                <p className="text-sm text-gray-500 mb-4">Add menu items to display on the Specials page</p>
                <Button
                  onClick={() => setShowAddToSpecialsDialog(true)}
                  variant="outline"
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add First Item
                </Button>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {specials.map((item) => (
                  <div key={item.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    {/* Mobile-friendly layout */}
                    <div className="flex items-center gap-3 mb-2">
                      {/* Image */}
                      <img 
                        src={item.image_url} 
                        alt={item.name} 
                        className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-lg flex-shrink-0" 
                      />
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-base sm:text-lg truncate">{item.name}</h4>
                        <p className="text-xs sm:text-sm text-gray-600">{item.category?.replace('_', ' ')}</p>
                        {item.price && <p className="text-pink-600 font-bold text-sm">${item.price.toFixed(2)}</p>}
                      </div>
                    </div>
                    
                    {/* Status Badges & Actions - Wrap on mobile */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => toggleFamous(item.id, item.famous)}
                        className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${
                          item.famous ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400' : 'bg-gray-100 text-gray-600 border-2 border-gray-300'
                        }`}
                      >
                        ⭐ Famous
                      </button>
                      <button
                        onClick={() => toggleFeatured(item.id, item.featured)}
                        className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${
                          item.featured ? 'bg-pink-100 text-pink-800 border-2 border-pink-400' : 'bg-gray-100 text-gray-600 border-2 border-gray-300'
                        }`}
                      >
                        ✨ Featured
                      </button>
                      <button
                        onClick={() => removeFromSpecials(item.id)}
                        className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-red-50 text-red-600 border-2 border-red-300 hover:bg-red-100 transition-colors"
                      >
                        ✕ Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
            <strong>💡 How it works:</strong>
            <ul className="mt-1 space-y-1">
              <li>• <strong>Famous</strong> = Signature items (always highlighted)</li>
              <li>• <strong>Featured</strong> = Seasonal or promotional items</li>
              <li>• <strong>Remove</strong> = Takes item off Specials page (doesn&apos;t delete the menu item)</li>
              <li>• To add items here, go to <strong>Menu Items</strong> and click &quot;⭐ Add to Specials&quot;</li>
            </ul>
          </div>
        </div>
      )}

      {/* ==========================================
          VIDEO TAB
          ========================================== */}
      {activeTab === 'video' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border-2 border-purple-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl">🎬</span>
              <div>
                <h3 className="text-xl font-bold">Featured Video</h3>
                <p className="text-sm text-gray-600">Manage the promotional video on the Specials page</p>
              </div>
            </div>

            {/* Video Title */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Video Title</label>
              <input
                type="text"
                value={featuredVideo.title || ''}
                onChange={(e) => setFeaturedVideo({ ...featuredVideo, title: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                placeholder="e.g., Death by Chocolate Stacker"
              />
            </div>

            {/* Video Subtitle */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Subtitle</label>
              <input
                type="text"
                value={featuredVideo.subtitle || ''}
                onChange={(e) => setFeaturedVideo({ ...featuredVideo, subtitle: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                placeholder="e.g., Our most indulgent creation!"
              />
            </div>

            {/* Video Upload/URL */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Video</label>
              
              {featuredVideo.video_url ? (
                <div className="mb-3">
                  <div className="relative rounded-lg overflow-hidden bg-black aspect-video max-w-md">
                    <video
                      src={featuredVideo.video_url}
                      className="w-full h-full object-cover"
                      controls
                      poster={featuredVideo.poster_url}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 break-all">{featuredVideo.video_url}</p>
                  <button
                    onClick={() => setFeaturedVideo({ ...featuredVideo, video_url: '' })}
                    className="text-red-500 text-sm mt-1 hover:underline"
                  >
                    Remove Video
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-500 transition-colors">
                  <span className="text-4xl mb-2">📹</span>
                  <p className="text-gray-500 mb-3">No video uploaded</p>
                  <label className="cursor-pointer px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg">
                    Upload Video
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
              
              {/* Manual URL input */}
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-1">Or paste video URL directly:</p>
                <input
                  type="text"
                  value={featuredVideo.video_url || ''}
                  onChange={(e) => setFeaturedVideo({ ...featuredVideo, video_url: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded focus:border-purple-500 focus:outline-none"
                  placeholder="https://images.1staveicp.com/video.mp4"
                />
              </div>
            </div>

            {/* Poster Image */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Poster Image (thumbnail before video plays)</label>
              
              <div className="flex gap-4 items-start">
                {featuredVideo.poster_url && (
                  <div className="relative">
                    <img
                      src={featuredVideo.poster_url}
                      alt="Video poster"
                      className="w-32 h-20 object-cover rounded-lg border"
                    />
                    <button
                      onClick={() => setFeaturedVideo({ ...featuredVideo, poster_url: '' })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                <div className="flex-1">
                  <div className="flex gap-2">
                    <label className="cursor-pointer px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">
                      Upload Poster
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePosterUpload}
                        className="hidden"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setImagePickerTarget('video-poster');
                        setShowImagePicker(true);
                      }}
                      className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm flex items-center gap-1"
                    >
                      <Image className="w-4 h-4" />
                      Gallery
                    </button>
                  </div>
                  <input
                    type="text"
                    value={featuredVideo.poster_url || ''}
                    onChange={(e) => setFeaturedVideo({ ...featuredVideo, poster_url: e.target.value })}
                    className="w-full mt-2 px-3 py-2 text-sm border rounded focus:border-purple-500 focus:outline-none"
                    placeholder="Or paste poster image URL..."
                  />
                </div>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg mb-4">
              <input
                type="checkbox"
                id="video_active"
                checked={featuredVideo.is_active !== false}
                onChange={(e) => setFeaturedVideo({ ...featuredVideo, is_active: e.target.checked })}
                className="w-5 h-5 accent-purple-500"
              />
              <label htmlFor="video_active" className="font-bold cursor-pointer">
                Show video on Specials page
              </label>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveFeaturedVideo}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white gap-2"
            >
              Save Featured Video
            </Button>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
            <strong>💡 Tips:</strong>
            <ul className="mt-1 space-y-1">
              <li>• Upload videos up to 50MB (MP4 recommended)</li>
              <li>• The poster image shows before the video plays</li>
              <li>• Videos play with sound when visitors click play</li>
              <li>• Toggle "Show video" off to hide without deleting</li>
            </ul>
          </div>
        </div>
      )}

      {/* Add to Specials Dialog */}
      {showAddToSpecialsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Add Item to Specials</h3>
              <button onClick={() => setShowAddToSpecialsDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search menu items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
              />
            </div>
            
            {/* Items List */}
            <div className="flex-1 overflow-y-auto">
              {allMenuItems
                .filter(item => !item.famous && !item.featured) // Only show items not already on specials
                .filter(item => 
                  item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  item.category?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((item) => (
                  <div 
                    key={item.id} 
                    className="flex items-center gap-4 p-3 border-b hover:bg-gray-50 transition-colors"
                  >
                    <img 
                      src={item.image_url} 
                      alt={item.name} 
                      className="w-12 h-12 object-cover rounded-lg" 
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold truncate">{item.name}</h4>
                      <p className="text-xs text-gray-500">{item.category?.replace('_', ' ')}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => addToSpecials(item.id, 'famous')}
                        className="px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium hover:bg-yellow-200"
                      >
                        + ⭐ Famous
                      </button>
                      <button
                        onClick={() => addToSpecials(item.id, 'featured')}
                        className="px-3 py-1.5 bg-pink-100 text-pink-800 rounded-full text-sm font-medium hover:bg-pink-200"
                      >
                        + ✨ Featured
                      </button>
                    </div>
                  </div>
                ))}
              
              {allMenuItems.filter(item => !item.famous && !item.featured).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  All menu items are already on Specials!
                </div>
              )}
            </div>
            
            <div className="mt-4 pt-4 border-t">
              <Button onClick={() => setShowAddToSpecialsDialog(false)} variant="outline" className="w-full">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Promotion Dialog with Drag & Drop */}
      {showPromoDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {editingPromo ? 'Edit Promotion' : `New ${promoTypes.find(t => t.value === promoForm.promo_type)?.label.slice(0, -1) || 'Promotion'}`}
              </h3>
              <button onClick={() => setShowPromoDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Type & Title Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-2">Type</label>
                  <select
                    value={promoForm.promo_type}
                    onChange={(e) => setPromoForm({ ...promoForm, promo_type: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  >
                    {promoTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.icon} {type.label.slice(0, -1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2">Title *</label>
                  <input
                    type="text"
                    value={promoForm.title}
                    onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                    placeholder="e.g., Win Free Ice Cream!"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-bold mb-2">Description *</label>
                <textarea
                  value={promoForm.description}
                  onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  rows="3"
                  placeholder="Describe your promotion, contest rules, or event details..."
                />
              </div>

              {/* Image Upload - Drag & Drop */}
              <div>
                <label className="block text-sm font-bold mb-2">Image (Drag & Drop or paste URL)</label>
                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  <strong>📐 Recommended:</strong> 800×600px (4:3 ratio) or 1200×630px (1.9:1 for social). Max 2MB. JPG or PNG.
                </div>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    isDragging ? 'border-pink-500 bg-pink-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {promoForm.image_url ? (
                    <div className="relative">
                      <img 
                        src={promoForm.image_url} 
                        alt="Preview" 
                        className="max-h-40 mx-auto rounded-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setPromoForm({ ...promoForm, image_url: '' })}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 transform translate-x-1/2 -translate-y-1/2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="py-4">
                      <p className="text-gray-500 mb-2">📷 Drag & drop an image here</p>
                      <p className="text-gray-400 text-sm mb-3">or</p>
                      <div className="flex justify-center gap-2">
                        <label className="cursor-pointer">
                          <span className="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1">
                            <Upload className="w-4 h-4" />
                            Upload
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setImagePickerTarget('promo');
                            setShowImagePicker(true);
                          }}
                          className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-lg font-medium inline-flex items-center gap-1"
                        >
                          <Image className="w-4 h-4" />
                          Gallery
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  value={promoForm.image_url.startsWith('data:') ? '' : promoForm.image_url}
                  onChange={(e) => setPromoForm({ ...promoForm, image_url: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none mt-2"
                  placeholder="Or paste image URL here..."
                />
              </div>

              {/* YouTube URL */}
              <div>
                <label className="block text-sm font-bold mb-2">📹 YouTube Video URL (optional)</label>
                <input
                  type="text"
                  value={promoForm.youtube_url}
                  onChange={(e) => setPromoForm({ ...promoForm, youtube_url: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                {promoForm.youtube_url && getYouTubeVideoId(promoForm.youtube_url) && (
                  <div className="mt-2 rounded-lg overflow-hidden">
                    <iframe
                      width="100%"
                      height="200"
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(promoForm.youtube_url)}`}
                      title="YouTube video preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-lg"
                    />
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-2">Start Date</label>
                  <input
                    type="date"
                    value={promoForm.start_date}
                    onChange={(e) => setPromoForm({ ...promoForm, start_date: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2">End Date</label>
                  <input
                    type="date"
                    value={promoForm.end_date}
                    onChange={(e) => setPromoForm({ ...promoForm, end_date: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={promoForm.is_active}
                  onChange={(e) => setPromoForm({ ...promoForm, is_active: e.target.checked })}
                  className="w-5 h-5 accent-pink-500"
                />
                <label htmlFor="is_active" className="font-bold cursor-pointer">
                  Active (visible on website)
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowPromoDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePromo}
                  className="flex-1 bg-pink-500 hover:bg-pink-600 text-white"
                  disabled={!promoForm.title || !promoForm.description}
                >
                  {editingPromo ? 'Save Changes' : 'Create Promotion'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Winner Dialog */}
      {showWinnerDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {editingWinner ? 'Edit Winner' : 'Add Winner'}
              </h3>
              <button onClick={() => setShowWinnerDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Winner Name */}
              <div>
                <label className="block text-sm font-bold mb-2">Winner Name *</label>
                <input
                  type="text"
                  value={winnerForm.name}
                  onChange={(e) => setWinnerForm({ ...winnerForm, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-yellow-500 focus:outline-none"
                  placeholder="John Smith"
                />
              </div>

              {/* Prize */}
              <div>
                <label className="block text-sm font-bold mb-2">Prize Won</label>
                <input
                  type="text"
                  value={winnerForm.prize}
                  onChange={(e) => setWinnerForm({ ...winnerForm, prize: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-yellow-500 focus:outline-none"
                  placeholder="$50 Gift Card"
                />
              </div>

              {/* Winner Image */}
              <div>
                <label className="block text-sm font-bold mb-2">Winner Photo</label>
                <div className="flex gap-4 items-center">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                    {winnerForm.image_url ? (
                      <img src={winnerForm.image_url} alt="Winner" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Trophy className="w-10 h-10 text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <label className="cursor-pointer">
                        <span className="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1">
                          <Upload className="w-4 h-4" />
                          {winnerForm.image_url ? 'Change' : 'Upload'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handleWinnerImageUpload}
                          className="hidden"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setImagePickerTarget('winner');
                          setShowImagePicker(true);
                        }}
                        className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-lg font-medium inline-flex items-center gap-1"
                      >
                        <Image className="w-4 h-4" />
                        Gallery
                      </button>
                    </div>
                    {winnerForm.image_url && (
                      <button 
                        onClick={() => setWinnerForm({ ...winnerForm, image_url: '' })}
                        className="text-red-500 text-sm"
                      >
                        Remove Photo
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Current Winner Toggle */}
              <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg">
                <input
                  type="checkbox"
                  id="is_current_winner"
                  checked={winnerForm.is_current}
                  onChange={(e) => setWinnerForm({ ...winnerForm, is_current: e.target.checked })}
                  className="w-5 h-5 accent-yellow-500"
                />
                <label htmlFor="is_current_winner" className="font-bold cursor-pointer">
                  This Week&apos;s Winner (displays prominently)
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <Button onClick={() => setShowWinnerDialog(false)} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveWinner}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white"
                  disabled={!winnerForm.name}
                >
                  {editingWinner ? 'Save Changes' : 'Add Winner'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Dialog */}
      {showEventDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {editingEvent ? 'Edit Event' : 'Add Event'}
              </h3>
              <button onClick={() => setShowEventDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Event Title */}
              <div>
                <label className="block text-sm font-bold mb-2">Event Title *</label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                  placeholder="Birthday Party Fun!"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-bold mb-2">Description</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                  rows="3"
                  placeholder="Describe the event..."
                />
              </div>

              {/* Event Images - Up to 6 */}
              <div>
                <label className="block text-sm font-bold mb-2">Event Photos (up to 6)</label>
                <div className="grid grid-cols-3 gap-3">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <div key={index} className="relative">
                      {eventForm.images?.[index] ? (
                        <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200">
                          <img 
                            src={eventForm.images[index]} 
                            alt={`Event ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => removeEventImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-500 cursor-pointer transition-colors">
                          <Upload className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-500 mt-1">{index + 1}</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => handleEventImageUpload(e)}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                <input
                  type="checkbox"
                  id="event_active"
                  checked={eventForm.is_active !== false}
                  onChange={(e) => setEventForm({ ...eventForm, is_active: e.target.checked })}
                  className="w-5 h-5 accent-purple-500"
                />
                <label htmlFor="event_active" className="font-bold cursor-pointer">
                  Active (visible on Specials page)
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <Button onClick={() => setShowEventDialog(false)} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEvent}
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                  disabled={!eventForm.title}
                >
                  {editingEvent ? 'Save Changes' : 'Add Event'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Picker Modal */}
      <ImagePickerModal
        open={showImagePicker}
        onClose={() => {
          setShowImagePicker(false);
          setImagePickerTarget(null);
        }}
        onSelect={(url) => {
          if (imagePickerTarget?.startsWith('offer-')) {
            const index = parseInt(imagePickerTarget.split('-')[1]);
            const newImages = [...(limitedOffer.images || [])];
            newImages[index] = url;
            setLimitedOffer({ ...limitedOffer, images: newImages.filter(Boolean) });
          } else if (imagePickerTarget === 'winner') {
            setWinnerForm({ ...winnerForm, image_url: url });
          } else if (imagePickerTarget === 'promo') {
            setPromoForm({ ...promoForm, image_url: url });
          } else if (imagePickerTarget?.startsWith('event-')) {
            const index = parseInt(imagePickerTarget.split('-')[1]);
            const newImages = [...(eventForm.images || [])];
            newImages[index] = url;
            setEventForm({ ...eventForm, images: newImages.filter(Boolean) });
          } else if (imagePickerTarget === 'video-poster') {
            setFeaturedVideo({ ...featuredVideo, poster_url: url });
          }
          setShowImagePicker(false);
          setImagePickerTarget(null);
        }}
        currentImage={
          imagePickerTarget?.startsWith('offer-') 
            ? limitedOffer.images?.[parseInt(imagePickerTarget.split('-')[1])] || ''
            : imagePickerTarget === 'winner'
              ? winnerForm.image_url
              : imagePickerTarget === 'promo'
                ? promoForm.image_url
                : imagePickerTarget?.startsWith('event-')
                  ? eventForm.images?.[parseInt(imagePickerTarget.split('-')[1])] || ''
                  : imagePickerTarget === 'video-poster'
                    ? featuredVideo.poster_url || ''
                    : ''
        }
      />
    </div>
  );
};

export default SpecialsAdmin;
